import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@astro/api'
import maharajPortrait from '../../../assets/images/maharaj-ji.png'
import { startingPrice } from './maharajPricingData'
import { useMaharajTiers } from './useMaharajTiers'

function unwrapAstrologerList(response) {
  if (Array.isArray(response)) return response
  if (response && typeof response === 'object' && Array.isArray(response.data)) return response.data
  return []
}

/** Upsell banner promoting the Raju Maharaj Ji priority-audience flow —
 * hidden on his own booking page since it would just point back at itself. */
export default function PremiumMaharajSection({ excludeId }) {
  const tiers = useMaharajTiers()
  const { data: searchData, isPending, isError } = useQuery({
    queryKey: ['astrologer-search', 'raju maharaj'],
    queryFn: () => api.astrologers.filter({ search: 'Raju Maharaj', per_page: 5 }),
    staleTime: 5 * 60 * 1000,
  })

  const maharaj = useMemo(() => {
    const list = unwrapAstrologerList(searchData)
    return list.find((a) => /maharaj/i.test(a.name || a.display_name || '')) ?? list[0] ?? null
  }, [searchData])

  if (isPending || isError) return null
  if (!maharaj || String(maharaj.id) === String(excludeId)) return null

  return (
    <section className="container px-6 mx-auto mt-16 relative">
      <div
        className="relative overflow-hidden rounded-3xl"
        style={{ background: 'linear-gradient(120deg,#2E0A10 0%,#4A0E15 55%,#2E0A10 100%)', border: '1px solid rgba(212,175,55,.35)' }}
      >
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden opacity-60">
          <div className="absolute -top-24 -right-16 rounded-full" style={{ width: 380, height: 380, background: 'radial-gradient(circle,rgba(245,210,140,.22) 0%,transparent 70%)', filter: 'blur(60px)' }} />
          <div className="absolute inset-0 opacity-[0.25]" style={{ backgroundImage: 'radial-gradient(circle,rgba(212,175,55,.3) 1px,transparent 1px)', backgroundSize: '36px 36px' }} />
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-8 p-8 sm:p-10">
          <Link to="/maharaj-ji" className="shrink-0">
            <img
              src={maharajPortrait}
              alt="Raju Maharaj Ji"
              className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border-2 shadow-[0_0_0_4px_rgba(212,175,55,.12)]"
              style={{ borderColor: 'rgba(212,175,55,.5)' }}
            />
          </Link>

          <div className="flex-1 min-w-0 text-center sm:text-left">
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-[.25em] text-[#1C140A] mb-3"
              style={{ background: 'linear-gradient(135deg,#D4AF37 0%,#F7D08F 100%)' }}
            >
              ✦ Premium Booking ✦
            </span>
            <h2 className="font-cinzel text-2xl sm:text-3xl font-normal leading-tight text-white">
              Get a Priority Audience with{' '}
              <Link to="/maharaj-ji" style={{ color: '#F0C868' }} className="hover:underline">Raju Maharaj Ji</Link>
            </h2>
            <p className="text-sm font-light mt-2 max-w-xl mx-auto sm:mx-0" style={{ color: 'rgba(255,255,255,.65)' }}>
              15+ years of Vedic mastery · 4,800+ verified clients · 98% success rate. Priority tiers set your fee by how soon you're seen.
            </p>
            <p className="text-xs uppercase tracking-widest mt-4" style={{ color: 'rgba(240,200,104,.85)' }}>
              Starting from ₹{startingPrice(tiers).toLocaleString('en-IN')}
            </p>
          </div>

          <Link
            to="/book-maharaj-ji"
            className="shrink-0 inline-flex items-center gap-3 font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 group"
            style={{ background: 'linear-gradient(135deg,#D4AF37 0%,#F7D08F 100%)', color: '#1C140A', boxShadow: '0 8px 32px -8px rgba(212,175,55,.5)' }}
          >
            Book Priority Audience
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  )
}
